import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { EditCompanyForm } from '@/components/companies/EditCompanyForm'
import { useCompany } from '@/hooks/useCompany'
import { Button } from '@/components/ui/button'

export default function EditCompany() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [showForm, setShowForm] = useState(true)

  const { company, loading, error } = useCompany(id || '')

  const handleSuccess = () => {
    setShowForm(false)
    // Navigate back to company management after short delay
    setTimeout(() => {
      navigate('/companies')
    }, 1500)
  }

  const handleCancel = () => {
    navigate('/companies')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
          <p className="mt-4 text-muted-foreground">Loading company details...</p>
        </div>
      </div>
    )
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-background py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="outline"
            onClick={() => navigate('/companies')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Companies
          </Button>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">
              Error Loading Company
            </h2>
            <p className="text-red-700 dark:text-red-300">
              {error || 'Company not found'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Convert CompanyDetail to Company type for EditCompanyForm
  const companyForForm = {
    id: company.id,
    name: company.name,
    description: company.description,
    website: company.website,
    logoUrl: company.logoUrl,
    createdAt: company.createdAt,
    updatedAt: company.updatedAt,
    employeeCount: company.employeeCount || null,
    revenueRange: company.revenueRange || null,
    businessBranches: company.businessBranches || null,
    aiPainPoints: company.aiPainPoints || null,
    aiAdoptionStage: company.aiAdoptionStage || null,
    isOnboarded: company.isOnboarded || null,
    isDemo: company.isDemo || null,
    _count: {
      users: company._count?.users || 0
    }
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/companies')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Companies
          </Button>
          <h1 className="text-3xl font-bold text-foreground">
            Edit Company
          </h1>
          <p className="mt-2 text-muted-foreground">
            Update company information and settings
          </p>
        </div>

        {/* Form */}
        <div className="bg-card rounded-lg shadow p-6">
          {showForm && (
            <EditCompanyForm
              company={companyForForm}
              onSuccess={handleSuccess}
              onCancel={handleCancel}
            />
          )}
        </div>
      </div>
    </div>
  )
}
